import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCorners,
} from "@dnd-kit/core";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowUpRight,
  Paperclip,
  GripVertical,
  Hash,
  CheckCircle2,
  Circle,
  Filter,
  X,
  CheckSquare,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Days helper for "≥N days since X" filters
const daysSince = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return (Date.now() - d.getTime()) / 86400000;
  } catch {
    return null;
  }
};

// Per-column filter presets. Each preset has a label + a predicate fn.
// `all` is the implicit default — no filter applied.
const COLUMN_FILTERS = {
  new: [
    {
      key: "stale_7d",
      label: "≥7 days waiting",
      test: (a) => (daysSince(a.received_at) ?? 0) >= 7,
    },
    {
      key: "form_only",
      label: "Online form only",
      test: (a) => a.source === "online_application",
    },
    {
      key: "resume_only",
      label: "Résumé only",
      test: (a) =>
        a.source !== "online_application" &&
        a.source !== "online_application_with_resume",
    },
  ],
  awaiting_response: [
    {
      key: "stale_7d",
      label: "≥7d since invite",
      test: (a) => (daysSince(a.email_sent_at) ?? 0) >= 7,
    },
    {
      key: "no_reminder",
      label: "No reminder sent",
      test: (a) => (a.reminders_sent_count || 0) === 0 && !!a.email_sent_at,
    },
    {
      key: "opened_no_reply",
      label: "Opened, no reply",
      test: (a) => !!a.email_opened_at,
    },
    {
      key: "reminded_3d",
      label: "≥3d since last reminder",
      test: (a) =>
        !!a.last_reminder_sent_at &&
        (daysSince(a.last_reminder_sent_at) ?? 0) >= 3,
    },
  ],
  interview_scheduled: [
    {
      key: "stale_3d",
      label: "≥3 days waiting",
      test: (a) => (daysSince(a.invited_at) ?? 0) >= 3,
    },
  ],
  // No filters for terminal columns — keep them clean.
  hired: [],
  not_hired: [],
  not_interested: [],
};

const COLUMNS = [
  { key: "new", label: "New", accent: "border-slate-300 bg-slate-50" },
  {
    key: "awaiting_response",
    label: "Awaiting Response",
    accent: "border-amber-300 bg-amber-50",
  },
  {
    key: "interview_scheduled",
    label: "Interview Scheduled",
    accent: "border-indigo-300 bg-indigo-50",
  },
  { key: "hired", label: "Hired", accent: "border-emerald-300 bg-emerald-50" },
  { key: "not_hired", label: "Not Hired", accent: "border-rose-300 bg-rose-50" },
  {
    key: "not_interested",
    label: "Not Interested",
    accent: "border-slate-300 bg-slate-50",
  },
];

const SOURCE_META = {
  online_application: {
    label: "Form",
    cls: "border-violet-300 bg-violet-100 text-violet-700",
  },
  online_application_with_resume: {
    label: "Form + Résumé",
    cls: "border-emerald-300 bg-emerald-100 text-emerald-700",
  },
  public_apply: {
    label: "Résumé",
    cls: "border-slate-300 bg-slate-100 text-slate-600",
  },
  default: {
    label: "Résumé",
    cls: "border-slate-300 bg-slate-100 text-slate-600",
  },
};

function scoreColor(score) {
  if (score == null) return "text-slate-400";
  if (score >= 80) return "text-emerald-700";
  if (score >= 60) return "text-blue-700";
  if (score >= 40) return "text-amber-700";
  return "text-rose-700";
}

function KanbanCard({ a, dragging = false, isSelected = false, onToggleSelect }) {
  const sourceMeta = SOURCE_META[a.source] || SOURCE_META.default;
  return (
    <div
      data-testid={`kanban-card-${a.id}`}
      className={`group rounded-sm border bg-white p-3 shadow-sm transition ${
        dragging
          ? "rotate-1 border-blue-400 shadow-lg"
          : isSelected
          ? "border-blue-500 ring-2 ring-blue-200"
          : "border-slate-200 hover:border-slate-300 hover:shadow"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        {onToggleSelect && (
          <button
            type="button"
            data-testid={`kanban-select-${a.id}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(a.id);
            }}
            className={`flex-none transition ${
              isSelected ? "text-blue-600" : "text-slate-300 hover:text-slate-500"
            }`}
            title={isSelected ? "Deselect" : "Select for bulk action"}
          >
            {isSelected ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <Circle className="h-4 w-4" />
            )}
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-slate-900">
              {a.candidate_name || "—"}
            </span>
            {a.source === "online_application_with_resume" && (
              <Paperclip className="h-3 w-3 flex-none text-emerald-700" />
            )}
          </div>
          <div className="mt-0.5 inline-flex items-center gap-1 font-mono text-[10px] font-semibold text-slate-500">
            <Hash className="h-2.5 w-2.5" />
            {a.application_ref || "—"}
          </div>
        </div>
        <GripVertical className="h-4 w-4 flex-none text-slate-300 opacity-0 transition group-hover:opacity-100" />
      </div>

      {a.position_applied && (
        <Link
          to={`/jobs/${a.job_id}`}
          onPointerDown={(e) => e.stopPropagation()}
          className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-blue-700"
        >
          <span className="truncate">{a.position_applied}</span>
          <ArrowUpRight className="h-2.5 w-2.5 flex-none opacity-50" />
        </Link>
      )}

      <div className="mt-2 flex items-center justify-between gap-2">
        <span
          className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] ${sourceMeta.cls}`}
        >
          {sourceMeta.label}
        </span>
        {typeof a.ai_score === "number" && (
          <span
            className={`text-xs font-bold tabular-nums ${scoreColor(a.ai_score)}`}
            title="AI score"
          >
            {Math.round(a.ai_score)}
            <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              {" "}
              /100
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

function DraggableCard({ a, isSelected, onToggleSelect }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: a.id, data: { applicant: a } });
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="cursor-grab touch-none active:cursor-grabbing"
    >
      <KanbanCard a={a} isSelected={isSelected} onToggleSelect={onToggleSelect} />
    </div>
  );
}

function KanbanColumn({
  column,
  items,
  selected,
  onToggleSelect,
  filter,
  onFilterChange,
  onSelectAllVisible,
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `col-${column.key}`,
    data: { columnKey: column.key },
  });

  const presets = COLUMN_FILTERS[column.key] || [];
  const activeFilter = filter ? presets.find((p) => p.key === filter) : null;
  const visible = activeFilter
    ? items.filter((a) => activeFilter.test(a))
    : items;

  const allVisibleIds = visible.map((a) => a.id);
  const allSelected =
    allVisibleIds.length > 0 && allVisibleIds.every((id) => selected.has(id));

  return (
    <section
      data-testid={`kanban-column-${column.key}`}
      className={`flex w-72 flex-none flex-col rounded-sm border ${column.accent}`}
    >
      <header className="flex items-center justify-between gap-2 border-b border-slate-200/70 px-3 py-2">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700">
          {column.label}
        </h3>
        <div className="flex items-center gap-1.5">
          {presets.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid={`kanban-filter-trigger-${column.key}`}
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-sm transition ${
                    activeFilter
                      ? "bg-blue-600 text-white"
                      : "text-slate-500 hover:bg-white/60 hover:text-slate-700"
                  }`}
                  title="Filter this column"
                >
                  <Filter className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                data-testid={`kanban-filter-menu-${column.key}`}
              >
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
                  Filter {column.label}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={filter || ""}
                  onValueChange={(v) =>
                    onFilterChange(column.key, v === "" ? null : v)
                  }
                >
                  <DropdownMenuRadioItem
                    value=""
                    data-testid={`kanban-filter-${column.key}-all`}
                  >
                    Show all
                  </DropdownMenuRadioItem>
                  <DropdownMenuSeparator />
                  {presets.map((p) => (
                    <DropdownMenuRadioItem
                      key={p.key}
                      value={p.key}
                      data-testid={`kanban-filter-${column.key}-${p.key}`}
                    >
                      {p.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <span
            data-testid={`kanban-count-${column.key}`}
            className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-bold tabular-nums text-slate-600"
            title={activeFilter ? `${visible.length} of ${items.length}` : ""}
          >
            {activeFilter ? `${visible.length} / ${items.length}` : items.length}
          </span>
        </div>
      </header>

      {activeFilter && (
        <div className="flex items-center justify-between gap-1 border-b border-slate-200/70 bg-blue-50/60 px-2 py-1">
          <span
            data-testid={`kanban-filter-pill-${column.key}`}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] text-blue-700"
          >
            <Filter className="h-2.5 w-2.5" />
            {activeFilter.label}
          </span>
          <button
            type="button"
            data-testid={`kanban-filter-clear-${column.key}`}
            onClick={() => onFilterChange(column.key, null)}
            className="text-blue-700 hover:text-blue-900"
            title="Clear filter"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      <div
        ref={setNodeRef}
        className={`flex flex-1 flex-col gap-2 p-2 transition ${
          isOver ? "bg-blue-50/60 ring-2 ring-inset ring-blue-300" : ""
        }`}
        style={{ minHeight: 120 }}
      >
        {visible.length === 0 && (
          <div className="py-6 text-center text-[10px] uppercase tracking-[0.18em] text-slate-400">
            {activeFilter ? "No matches" : "Drop here"}
          </div>
        )}
        {visible.map((a) => (
          <DraggableCard
            key={a.id}
            a={a}
            isSelected={selected.has(a.id)}
            onToggleSelect={onToggleSelect}
          />
        ))}
      </div>

      {visible.length > 1 && (
        <footer className="border-t border-slate-200/70 px-2 py-1.5">
          <button
            type="button"
            data-testid={`kanban-select-all-${column.key}`}
            onClick={() => onSelectAllVisible(allVisibleIds, !allSelected)}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-sm py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600 transition hover:bg-white/60 hover:text-slate-900"
          >
            <CheckSquare className="h-3 w-3" />
            {allSelected ? "Deselect all visible" : `Select all visible (${visible.length})`}
          </button>
        </footer>
      )}
    </section>
  );
}

export default function ApplicantsKanban({
  applicants,
  onStatusChange,
  selected,
  onToggleSelect,
  onToggleManySelected,
}) {
  const [activeId, setActiveId] = useState(null);
  const [columnFilters, setColumnFilters] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      return JSON.parse(localStorage.getItem("hireloop_kanban_filters") || "{}");
    } catch {
      return {};
    }
  });

  const onFilterChange = (colKey, value) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (value) next[colKey] = value;
      else delete next[colKey];
      try {
        localStorage.setItem("hireloop_kanban_filters", JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const handleSelectAllVisible = (ids, shouldSelect) => {
    if (onToggleManySelected) onToggleManySelected(ids, shouldSelect);
  };
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor)
  );

  const byColumn = useMemo(() => {
    const buckets = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    applicants.forEach((a) => {
      const s = a.application_status || "new";
      if (buckets[s]) buckets[s].push(a);
      else buckets.new.push(a);
    });
    return buckets;
  }, [applicants]);

  const activeApplicant = useMemo(
    () => applicants.find((a) => a.id === activeId) || null,
    [applicants, activeId]
  );

  const handleDragEnd = (evt) => {
    setActiveId(null);
    const { active, over } = evt;
    if (!over || !active) return;
    const newStatus = over.data?.current?.columnKey;
    const applicant = active.data?.current?.applicant;
    if (!applicant || !newStatus) return;
    if ((applicant.application_status || "new") === newStatus) return;
    onStatusChange(applicant.id, newStatus);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={handleDragEnd}
    >
      <div
        data-testid="applicants-kanban-board"
        className="mt-6 flex gap-3 overflow-x-auto pb-4"
      >
        {COLUMNS.map((c) => (
          <KanbanColumn
            key={c.key}
            column={c}
            items={byColumn[c.key]}
            selected={selected || new Set()}
            onToggleSelect={onToggleSelect}
            filter={columnFilters[c.key]}
            onFilterChange={onFilterChange}
            onSelectAllVisible={handleSelectAllVisible}
          />
        ))}
      </div>
      <DragOverlay>
        {activeApplicant ? <KanbanCard a={activeApplicant} dragging /> : null}
      </DragOverlay>
    </DndContext>
  );
}
